import { useEffect, useRef, useState } from "react";
import { Bluetooth, Camera, Upload, Video, Save, X, Square } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { uuid } from "@/lib/format";
import { today } from "@/lib/clinic";
import { uploadFile, useFileMeta } from "@/lib/files";
import { BT_DEVICES, btSupported, readBluetooth } from "@/lib/devices";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";

export const IMAGE_TYPES = ["ultrasound", "dermatoscope", "wound", "ecg", "xray", "scan_doc", "other"];
const nowTime = () => new Date().toTimeString().slice(0, 5);

function BluetoothPanel({ patient, onSaved }) {
  const { t, dispatch, staff } = useApp();
  const [vitals, setVitals] = useState({});
  const [dev, setDev] = useState("");
  const [busy, setBusy] = useState("");
  const stopRef = useRef(null);
  useEffect(() => () => stopRef.current?.(), []);
  const connect = async (k) => {
    if (!btSupported()) return toast.error(t("bt_unsupported"));
    setBusy(k);
    try { stopRef.current?.(); stopRef.current = await readBluetooth(k, (v, name) => { setVitals((p) => ({ ...p, ...v })); setDev(name); toast.success(`${name}: ${Object.entries(v).map(([a, b]) => `${t(`vital_${a}`)} ${b}`).join(" · ")}`); }); }
    catch (e) { if (e?.name !== "NotFoundError") toast.error(e.message || t("bt_failed")); }
    finally { setBusy(""); }
  };
  const save = () => {
    if (!Object.keys(vitals).length) return;
    const result = Object.entries(vitals).map(([k, v]) => `${t(`vital_${k}`)} ${v}`).join(" · ");
    if (dispatch("record.upsert", { id: uuid(), patient_id: patient.id, patient_name: patient.name, kind: "device", name: dev || t("bt_device"), result, vitals, date: today(), time: nowTime(), nurse_name: staff.name, next_date: "" })) { toast.success(t("saved")); setVitals({}); onSaved?.(); }
  };
  return (
    <section className="rounded border border-[#CCCCCC] bg-white p-3 space-y-2" data-testid="bt-panel">
      <h3 className="font-bold text-sm flex items-center gap-1"><Bluetooth className="w-4 h-4 text-[#2B79C2]" />{t("bt_vitals")}</h3>
      {!btSupported() && <p className="text-xs text-[#C7353B]" data-testid="bt-unsupported">{t("bt_unsupported")}</p>}
      <div className="flex flex-wrap gap-1.5">{Object.keys(BT_DEVICES).map((k) => <Chip key={k} active={busy === k} onClick={() => connect(k)} data-testid={`bt-connect-${k}`}><Bluetooth className="w-4 h-4 me-1" />{t(`btdev_${k}`)}</Chip>)}</div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {["bp", "pulse", "spo2", "temp", "glucose", "weight"].map((k) => <Field key={k} label={t(`vital_${k}`)}><input dir="ltr" value={vitals[k] || ""} onChange={(e) => setVitals({ ...vitals, [k]: e.target.value })} className={`${inputCls} !h-10 text-center`} data-testid={`bt-vital-${k}`} /></Field>)}
      </div>
      <BigButton onClick={save} disabled={!Object.keys(vitals).length} className="w-full !h-12" data-testid="bt-save-btn"><Save className="w-5 h-5" />{t("save_reading")}</BigButton>
    </section>
  );
}

function CapturePanel({ patient, onSaved }) {
  const { t, dispatch, staff } = useApp();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const [cams, setCams] = useState([]);
  const [cam, setCam] = useState("");
  const [on, setOn] = useState(false);
  const [rec, setRec] = useState(false);
  const [type, setType] = useState("ultrasound");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const stop = () => { streamRef.current?.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; setOn(false); };
  useEffect(() => () => stop(), []);
  const start = async () => {
    try {
      stop();
      const s = await navigator.mediaDevices.getUserMedia({ video: cam ? { deviceId: { exact: cam } } : { facingMode: "environment" }, audio: false });
      streamRef.current = s; videoRef.current.srcObject = s; setOn(true);
      const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput"); setCams(list);
    } catch (e) { toast.error(t("camera_failed")); }
  };
  const saveBlob = async (blob, ext) => {
    setBusy(true);
    try {
      const f = new File([blob], `${type}-${Date.now()}.${ext}`, { type: blob.type });
      const up = await uploadFile(f, "clinic");
      if (dispatch("record.upsert", { id: uuid(), patient_id: patient.id, patient_name: patient.name, kind: "image", image_type: type, name: t(`img_${type}`), result: note, file_id: up.id, content_type: up.content_type, date: today(), time: nowTime(), nurse_name: staff.name, next_date: "" })) { toast.success(t("saved")); setNote(""); onSaved?.(); }
    } catch (e) { toast.error(t("upload_failed")); } finally { setBusy(false); }
  };
  const snap = () => { const v = videoRef.current; const c = document.createElement("canvas"); c.width = v.videoWidth; c.height = v.videoHeight; c.getContext("2d").drawImage(v, 0, 0); c.toBlob((b) => b && saveBlob(b, "jpg"), "image/jpeg", 0.9); };
  const record = () => {
    if (rec) { recRef.current?.stop(); return; }
    const chunks = []; const mr = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
    mr.ondataavailable = (e) => chunks.push(e.data);
    mr.onstop = () => { setRec(false); saveBlob(new Blob(chunks, { type: "video/webm" }), "webm"); };
    mr.start(); recRef.current = mr; setRec(true);
    setTimeout(() => mr.state === "recording" && mr.stop(), 15000);
  };
  return (
    <section className="rounded border border-[#CCCCCC] bg-white p-3 space-y-2" data-testid="capture-panel">
      <h3 className="font-bold text-sm flex items-center gap-1"><Camera className="w-4 h-4 text-[#1F9D55]" />{t("device_capture")}</h3>
      <p className="text-xs text-[#555]">{t("device_capture_hint")}</p>
      <div className="flex flex-wrap gap-1.5">{IMAGE_TYPES.map((k) => <Chip key={k} active={type === k} onClick={() => setType(k)} data-testid={`img-type-${k}`}>{t(`img_${k}`)}</Chip>)}</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
        <Field label={t("video_source")}><select value={cam} onChange={(e) => setCam(e.target.value)} className={inputCls} data-testid="camera-select"><option value="">{t("default_camera")}</option>{cams.map((c) => <option key={c.deviceId} value={c.deviceId}>{c.label || c.deviceId.slice(0, 8)}</option>)}</select></Field>
        <BigButton variant={on ? "soft" : "green"} onClick={on ? stop : start} className="!h-12" data-testid="camera-toggle-btn">{on ? <X className="w-5 h-5" /> : <Video className="w-5 h-5" />}{on ? t("stop") : t("start_camera")}</BigButton>
      </div>
      <video ref={videoRef} autoPlay playsInline muted className={`w-full max-h-[360px] bg-black rounded ${on ? "" : "hidden"}`} data-testid="camera-preview" />
      {on && (
        <>
          <Field label={t("hr_note")}><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="capture-note-input" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <BigButton onClick={snap} disabled={busy} className="!h-12" data-testid="capture-photo-btn"><Camera className="w-5 h-5" />{t("take_photo")}</BigButton>
            <BigButton variant={rec ? "danger" : "amber"} onClick={record} disabled={busy} className="!h-12" data-testid="capture-video-btn">{rec ? <Square className="w-5 h-5" /> : <Video className="w-5 h-5" />}{rec ? t("stop_recording") : t("record_clip")}</BigButton>
          </div>
        </>
      )}
    </section>
  );
}

function ImportPanel({ patient, onSaved }) {
  const { t, dispatch, staff } = useApp();
  const [type, setType] = useState("scan_doc");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try {
        const file = f.name.toLowerCase().endsWith(".dcm") && !f.type ? new File([f], f.name, { type: "application/dicom" }) : f;
        const up = await uploadFile(file, "clinic");
        if (dispatch("record.upsert", { id: uuid(), patient_id: patient.id, patient_name: patient.name, kind: "image", image_type: type, name: t(`img_${type}`), result: note, file_id: up.id, content_type: up.content_type, file_name: f.name, date, time: nowTime(), nurse_name: staff.name, next_date: "" })) ok += 1;
      } catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || t("upload_failed")}`); }
    }
    setBusy(false);
    if (ok) { toast.success(t("files_attached", { n: ok })); setNote(""); onSaved?.(); }
  };
  return (
    <section className="rounded border border-[#CCCCCC] bg-white p-3 space-y-2" data-testid="import-panel">
      <h3 className="font-bold text-sm flex items-center gap-1"><Upload className="w-4 h-4 text-[#8E44AD]" />{t("device_import")}</h3>
      <p className="text-xs text-[#555]">{t("device_import_hint")}</p>
      <div className="flex flex-wrap gap-1.5">{IMAGE_TYPES.map((k) => <Chip key={k} active={type === k} onClick={() => setType(k)} data-testid={`import-type-${k}`}>{t(`img_${k}`)}</Chip>)}</div>
      <div className="grid sm:grid-cols-3 gap-2">
        <Field label={t("hr_date")}><input type="date" dir="ltr" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="import-date-input" /></Field>
        <Field label={t("hr_note")}><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid="import-note-input" /></Field>
        <label className={`flex items-end ${busy ? "opacity-50 pointer-events-none" : ""}`}><span className="ak-btn h-12 w-full rounded-full bg-[#8E44AD] text-white font-bold flex items-center justify-center gap-2 cursor-pointer"><Upload className="w-5 h-5" />{busy ? "…" : t("choose_files")}<input type="file" multiple accept="image/*,application/pdf,video/*,.dcm,application/dicom" onChange={onFiles} className="hidden" data-testid="import-file-input" /></span></label>
      </div>
    </section>
  );
}

function Thumb({ rec, onOpen }) {
  const { t } = useApp();
  const meta = useFileMeta(rec.file_id);
  const isImg = (rec.content_type || meta?.type || "").startsWith("image/");
  const isVid = (rec.content_type || "").startsWith("video/");
  return (
    <button type="button" onClick={() => onOpen(rec, meta?.url)} className="rounded border border-[#CCCCCC] bg-white overflow-hidden text-start hover:ring-2 hover:ring-[#2B79C2]" data-testid="device-image-thumb">
      <div className="aspect-square bg-[#F0F0F0] grid place-items-center overflow-hidden">
        {isImg && meta?.url ? <img src={meta.url} alt={rec.name} className="w-full h-full object-cover" /> : isVid && meta?.url ? <video src={meta.url} muted className="w-full h-full object-cover" /> : <span className="text-xs font-bold text-[#555] px-2 text-center">{rec.file_name || rec.content_type || "DICOM"}</span>}
      </div>
      <div className="p-1.5 text-xs"><div className="font-bold truncate">{t(`img_${rec.image_type || "other"}`)}</div><div className="text-[#666]" dir="ltr">{rec.date} {rec.time}</div>{rec.result && <div className="truncate text-[#333]">{rec.result}</div>}</div>
    </button>
  );
}

function Viewer({ item, onClose }) {
  const { t, dispatch, can } = useApp();
  if (!item) return null;
  const { rec, url } = item;
  const isImg = (rec.content_type || "").startsWith("image/"), isVid = (rec.content_type || "").startsWith("video/");
  const del = () => { if (window.confirm(t("confirm_delete_file"))) { dispatch("record.upsert", { ...rec, deleted: true }); onClose(); } };
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex flex-col" onClick={onClose} data-testid="device-viewer">
      <div className="flex items-center gap-3 p-3 text-white text-sm" onClick={(e) => e.stopPropagation()}>
        <span className="font-bold">{t(`img_${rec.image_type || "other"}`)}</span><span dir="ltr">{rec.date} {rec.time}</span><span className="opacity-80 truncate">{rec.result}</span><span className="opacity-60">{rec.nurse_name}</span>
        <div className="ms-auto flex gap-2">{url && <a href={url} download={rec.file_name || `${rec.image_type}-${rec.date}`} className="px-3 h-9 rounded bg-white/10 grid place-items-center font-bold" data-testid="device-download-btn">{t("download")}</a>}{can("medical") && <button type="button" onClick={del} className="px-3 h-9 rounded bg-[#C7353B] font-bold" data-testid="device-delete-btn">{t("delete")}</button>}<button type="button" onClick={onClose} className="w-9 h-9 rounded bg-white/10 grid place-items-center" data-testid="device-viewer-close"><X className="w-5 h-5" /></button></div>
      </div>
      <div className="flex-1 min-h-0 grid place-items-center p-3">
        {isImg && url && <img src={url} alt="" className="max-w-full max-h-full object-contain" />}
        {isVid && url && <video src={url} controls autoPlay className="max-w-full max-h-full" />}
        {!isImg && !isVid && url && (rec.content_type === "application/pdf" ? <iframe title="pdf" src={url} className="w-full h-full bg-white rounded" /> : <p className="text-white">{t("dicom_download_hint")}</p>)}
      </div>
    </div>
  );
}

export default function DevicesTab({ patient, records }) {
  const { t, can } = useApp();
  const [open, setOpen] = useState(null);
  const images = records.filter((r) => r.kind === "image" && r.file_id);
  const devs = records.filter((r) => r.kind === "device");
  return (
    <div className="space-y-3" data-testid="devices-tab">
      {can("medical") && <div className="grid lg:grid-cols-2 gap-3"><BluetoothPanel patient={patient} /><ImportPanel patient={patient} /></div>}
      {can("medical") && <CapturePanel patient={patient} />}
      <section className="space-y-2">
        <h3 className="font-bold text-sm">{t("patient_images")} ({images.length})</h3>
        {images.length === 0 && <p className="text-sm text-muted-foreground">{t("no_images_yet")}</p>}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="device-gallery">{images.map((r) => <Thumb key={r.id} rec={r} onOpen={(rec, url) => setOpen({ rec, url })} />)}</div>
      </section>
      {devs.length > 0 && (
        <section className="space-y-1" data-testid="device-readings">
          <h3 className="font-bold text-sm">{t("device_readings")} ({devs.length})</h3>
          {devs.map((r) => <div key={r.id} className="rounded border border-[#E5E5E5] bg-white px-3 py-2 text-sm flex flex-wrap gap-2 items-center" data-testid="device-reading-row"><span dir="ltr" className="text-[#666]">{r.date} {r.time}</span><span className="px-2 py-0.5 rounded bg-[#E5F1FB] text-[#1F4E8C] text-xs font-bold">{r.name}</span><span className="font-bold">{r.result}</span><span className="ms-auto text-xs text-[#666]">{r.nurse_name}</span></div>)}
        </section>
      )}
      <Viewer item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
